export type AppointmentStatus =
  | 'SCHEDULED'
  | 'CHECKED_IN'
  | 'WAITING'
  | 'WITH_NURSE'
  | 'VITALS_COMPLETE'
  | 'WITH_DOCTOR'
  | 'TREATMENT_COMPLETE'
  | 'PENDING_VERIFICATION'
  | 'VERIFICATION_COMPLETE'
  | 'CHECKOUT'
  | 'COMPLETED'
  | 'CANCELLED';

export type VerificationStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'REJECTED';

export interface VitalSigns {
  id?: string;
  appointmentId: string;
  patientId: string;
  recordedBy: string;
  temperature: number;
  bloodPressureSystolic: number;
  bloodPressureDiastolic: number;
  heartRate?: number;
  respiratoryRate?: number;
  oxygenSaturation?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  painLevel?: number;
  notes?: string;
  recordedAt: string;
}

export interface SOAPNotes {
  id?: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  notes?: string;
  diagnoses?: Array<{
    id: string;
    code: string;
    description: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
  // Extended properties from the actual API response
  workflowId?: string;
  recordedAt?: string;
  recordedBy?: string;
}

export interface PrescriptionItem {
  id: string;
  prescriptionId: string;
  medicineId: string;
  medicineName?: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions?: string;
  quantity: number;
  totalPrice?: number;
  medicine?: {
    id: string;
    name: string;
    genericName?: string;
    manufacturer?: string;
    strength?: string;
    form?: string;
    unit?: string;
    price?: number;
    unitPrice?: number;
    isActive: boolean;
    requiresPrescription: boolean;
    category?: string;
    sideEffects?: string;
    contraindications?: string;
    interactions?: string;
    storageInstructions?: string;
    expiryDate?: string;
    batchNumber?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface Prescription {
  id?: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  medicineId: string;
  medicineName: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  quantity: number;
  refills: number;
  isActive: boolean;
  prescribedAt: string;
  items?: PrescriptionItem[];
  // Extended properties from the actual API response
  treatmentId?: string;
  workflowId?: string;
  issuedDate?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: 'PENDING' | 'FULFILLED' | 'CANCELLED' | 'COMPLETED' | 'PROCESSED';
  verificationStatus?: VerificationStatus;
  verifiedBy?: string;
  verifiedAt?: string;
  verificationNotes?: string;
  insuranceCoverage?: {
    isApproved: boolean;
    approvedAmount: number;
    copayAmount: number;
    deductibleAmount: number;
    notes?: string;
  };
  paymentCollected?: {
    amount: number;
    method: 'CASH' | 'CARD' | 'INSURANCE' | 'MIXED';
    collectedBy: string;
    collectedAt: string;
  };
  doctor?: {
    id: string;
    firstName: string;
    lastName: string;
    specialization?: string;
  };
  patient?: {
    id: string;
    firstName: string;
    lastName: string;
    patientId: string;
  };
  processedAt?: string;
  processingNotes?: string;
  type?: 'ADMISSION_DISPENSING' | 'OUTPATIENT';
  admission?: {
    admissionId: string;
  };
  wardInfo?: {
    wardNumber: string;
    bedNumber: string;
    roomType: string;
    department: string;
    location: string;
  };
  dispensing?: {
    quantityRequested: number;
    quantityDispensed: number;
  };
}

export interface LabOrder {
  id?: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  testName: string;
  testCode: string;
  testTypeId?: string;
  category: string;
  priority: 'ROUTINE' | 'URGENT' | 'STAT';
  instructions?: string;
  status: 'ORDERED' | 'COLLECTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  verificationStatus?: VerificationStatus;
  verifiedBy?: string;
  verifiedAt?: string;
  verificationNotes?: string;
  items?: Array<{
    id: string;
    testTypeId: string;
    testType?: {
      id: string;
      name: string;
      code: string;
      cost: string;
      department: string;
      description?: string;
    };
  }>;
  insuranceCoverage?: {
    isApproved: boolean;
    approvedAmount: number;
    copayAmount: number;
    deductibleAmount: number;
    notes?: string;
  };
  paymentCollected?: {
    amount: number;
    method: 'CASH' | 'CARD' | 'INSURANCE' | 'MIXED';
    collectedBy: string;
    collectedAt: string;
  };
  orderedAt: string;
  scheduledFor?: string;
  collectedAt?: string;
  completedAt?: string;
  result?: string;
  normalRange?: string;
  isAbnormal?: boolean;
}

export interface AppointmentWorkflow {
  id: string;
  appointmentId: string;
  patientId: string;
  patient?: {
    id: string;
    patientId: string;
    firstName: string;
    lastName: string;
  };
  doctorId?: string;
  nurseId?: string;
  status: AppointmentStatus;
  checkedInAt?: string;
  vitalsRecordedAt?: string;
  doctorStartedAt?: string;
  treatmentCompletedAt?: string;
  checkedOutAt?: string;
  completedAt?: string;
  currentStep: number;
  totalSteps: number;
  vitalSigns?: VitalSigns;
  soapNotes?: SOAPNotes;
  prescriptions: Prescription[];
  labOrders: LabOrder[];
  treatments?: Array<{
    id: string;
    diagnosis: string;
    icd10Code: string;
    symptoms: string;
    procedures?: Array<{
      id: string;
      type: {
        name: string;
        code: string;
        cost: number;
      };
    }>;
  }>;
  notes?: string;
  waitingTime?: number;
  treatmentDuration?: number;
  checkoutSummary?: {
    consultationFee: number;
    prescriptionFees: number;
    labFees: number;
    procedureFees: number;
    totalAmount: number;
    hasInsurance: boolean;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceAmount?: number;
    cashAmount?: number;
    coverageBreakdown?: any;
    insuranceCategory?: {
      id: string;
      name: string;
      code: string;
      coverageRules?: Array<{
        id: string;
        treatmentType: string;
        procedureCode?: string;
        coveragePercentage: number;
        maxAmount?: number;
        maxFrequency?: number;
        frequencyPeriod?: string;
        requiresPreAuth: boolean;
      }>;
    };
    appliedCoverageRules?: Array<{
      treatmentType: string;
      procedureCode?: string;
      originalAmount: number;
      coveredAmount: number;
      coveragePercentage: number;
      ruleApplied: string;
      requiresPreAuth: boolean;
    }>;
  };
  // Extended properties from the actual API response
  appointment?: {
    id: string;
    appointmentId: string;
    patientId: string;
    doctorId: string;
    departmentId: string;
    appointmentDate: string;
    startTime: string;
    endTime: string;
    type: string;
    status: string;
    reason?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
    patient?: {
      id: string;
      userId?: string;
      patientId: string;
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      gender: string;
      bloodGroup: string;
      phone: string;
      email: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      emergencyContact?: {
        name: string;
        email: string;
        phone: string;
        relationship: string;
      };
      medicalHistory?: string;
      allergies?: string;
      currentMedications?: string;
      paymentType: string;
      preferredPayment: string;
      insurancePolicies?: Array<{
        id: string;
        provider: {
          name: string;
        };
        policyNumber: string;
        policyType: string;
        coverageAmount: number;
        deductible: number;
        isActive: boolean;
        category: {
          name: string;
          coveragePercentage: number;
          copayAmount: number;
          maxPerVisitLimit?: number;
          maxAnnualLimit?: number;
          deductibleAmount: number;
        };
      }>;
      createdAt: string;
      updatedAt: string;
    };
    doctor?: {
      id: string;
      userId: string;
      licenseNumber: string;
      firstName: string;
      lastName: string;
      specialization: string;
      qualification: string;
      experience: number;
      phone: string;
      email: string;
      address: string;
      consultationFee: string;
      isAvailable: boolean;
      createdAt: string;
      updatedAt: string;
    };
    department?: {
      id: string;
      name: string;
      code: string;
      location: string;
      description: string;
      createdAt: string;
      updatedAt: string;
    };
  };
}

export interface WorkflowStep {
  id: number;
  name: string;
  title: string;
  description: string;
  role: 'RECEPTIONIST' | 'NURSE' | 'DOCTOR' | 'ADMIN';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
  startedAt?: string;
  completedAt?: string;
  assignedTo?: string;
  estimatedDuration: number;
}

export const WORKFLOW_STEPS: Record<number, Omit<WorkflowStep, 'status' | 'startedAt' | 'completedAt' | 'assignedTo'>> = {
  1: {
    id: 1,
    name: 'CHECK_IN',
    title: 'Check-In',
    description: 'Patient arrives and checks in',
    role: 'RECEPTIONIST',
    estimatedDuration: 5
  },
  2: {
    id: 2,
    name: 'WAITING',
    title: 'Waiting Area',
    description: 'Patient waits to be called',
    role: 'NURSE',
    estimatedDuration: 15
  },
  3: {
    id: 3,
    name: 'VITALS',
    title: 'Vital Signs',
    description: 'Nurse records vital signs',
    role: 'NURSE',
    estimatedDuration: 10
  },
  4: {
    id: 4,
    name: 'TRIAGE',
    title: 'Doctor Consultation',
    description: 'Doctor examines patient and creates SOAP notes',
    role: 'DOCTOR',
    estimatedDuration: 30
  },
  5: {
    id: 5,
    name: 'PRESCRIPTIONS',
    title: 'Prescriptions',
    description: 'Doctor prescribes medications',
    role: 'DOCTOR',
    estimatedDuration: 5
  },
  6: {
    id: 6,
    name: 'LAB_ORDERS',
    title: 'Lab Orders',
    description: 'Doctor orders laboratory tests',
    role: 'DOCTOR',
    estimatedDuration: 5
  },
  7: {
    id: 7,
    name: 'VERIFICATION',
    title: 'Prescription & Lab Verification',
    description: 'Receptionist verifies prescriptions and lab orders',
    role: 'RECEPTIONIST',
    estimatedDuration: 10
  },
  8: {
    id: 8,
    name: 'CHECKOUT',
    title: 'Checkout',
    description: 'Patient checkout and billing',
    role: 'RECEPTIONIST',
    estimatedDuration: 10
  }
};